/*
  UV hoodie buy box (copy of the glove buy box, see docs/uvh-build). Pattern > Fit > Style > Size.
  1 hoodie, or 3 hoodies with the cheapest free (buy 2, get 1 free). Hoodie 2 and 3 copy the top
  choice until the shopper swaps them. Size is never chosen for the customer.
*/
(() => {
  if (customElements.get('fd-uvh-box')) return;

  const SIZE_ORDER = ['xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', 'xxxl', '3xl'];
  const FITS = [['Women', "Women's"], ['Men', "Men's"]];
  const fitLabel = fit => (FITS.find(([f]) => f === fit) || [, ''])[1];
  // Style comes from the product type: UV Sun Hoodie = Hoodie, UV Crew Shirt = Crew, UV Zip Sun Hoodie = Zip
  const CUTS = ['Hoodie', 'Crew', 'Zip'];
  const cutOf = (type, title) => { const t = (String(type || '') + ' ' + String(title || '')).toLowerCase(); return /zip/.test(t) ? 'Zip' : /crew/.test(t) ? 'Crew' : 'Hoodie'; };
  const BUNDLE = 3; // the second choice is 3 hoodies

  class UvhBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.styles = this.groupProducts(JSON.parse(this.querySelector('[data-products]').textContent));
      this.discount = Number(this.dataset.discount) || 0;
      this.giftValue = Number(this.dataset.giftValue) || 0;
      this.swatchStyle = this.dataset.swatchStyle || 'photo';
      this.tier = 2; // 2 = the 3-hoodie choice
      // Pattern order from the section setting (e.g. "Topo Geo, Midnight Wolves, ..."); unlisted patterns keep their place after.
      const order = (this.dataset.patternOrder || '').split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      const rankOf = s => { const i = order.indexOf(s.name.toLowerCase()); return i < 0 ? order.length : i; };
      this.styles = this.styles.map((s, i) => [s, i]).sort((a, b) => rankOf(a[0]) - rankOf(b[0]) || a[1] - b[1]).map(([s]) => s);
      // Open on the glove the shopper landed on; otherwise the default fit's first pattern.
      const here = this.styles.find(s => s.key === String(this.dataset.currentProduct || ''));
      const fit = here ? here.fit : (this.styles.some(s => s.fit === this.dataset.defaultFit) ? this.dataset.defaultFit : '');
      const start = here || this.style(this.firstStyle(fit));
      this.top = { fit, cut: start?.cut || 'Hoodie', size: '', style: start?.key || '' };
      // Hoodie 2 and Hoodie 3 of the 3-pack
      this.extras = [1, 2].map(() => ({ fit: this.top.fit, cut: this.top.cut, size: '', style: this.top.style, custom: false }));
      this.sheet = null; // draft choice while the picker sheet is open
      this.busy = false;
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-status]');
      // Sneak peek: no tiers, no buying; a coming soon button and an early-bird signup instead
      this.prelaunch = !!this.querySelector('.fdw2-soon');
      this.button = this.prelaunch ? null : this.form.querySelector('[type=submit]');
      this.label = this.querySelector('[data-atc-label]');
      this.setupSignup();
      // Batch card position (section setting): right under the buy button (Kaz, Oct 12) or at the top of the form
      const batches = this.querySelector('.fdw2-presale');
      if (batches?.dataset.pos === 'below') this.form.querySelector('.fdw-atc')?.after(batches);
      this.applyBatchTime();
      document.addEventListener('click', e => { const a = e.target.closest?.('a[href="#size-guide"]'); if (a && this.openSizeGuidePopup()) e.preventDefault(); });
      // Title block sits above the photo on phones, beside it (top of the right column) on desktop
      this.head = this.querySelector('[data-head]');
      this.mq = window.matchMedia('(max-width: 749px)');
      this.placeHead = () => {
        if (!this.head) return;
        const gallery = this.querySelector('.fdw-gallery'), copy = this.querySelector('.fdw-buy-copy');
        if (this.mq.matches) gallery?.before(this.head); else copy?.prepend(this.head);
      };
      this.placeHead();
      this.mq.addEventListener?.('change', this.placeHead);
      // Details / Features / Fabric accordion: under the buy box in the right column on desktop (like Poncho),
      // back in its own full-width spot on phones. It is the template section with the id "details".
      [['hoodie', 'Hoodie'], ['zip', 'Zip'], ['crew', 'Crew']].forEach(([id, cut]) => document.querySelectorAll('.shopify-section[id$="__hotspots_' + id + '"]').forEach(el => { el.setAttribute('data-show-for', cut); if (!window.Shopify?.designMode) el.hidden = true; }));
      this.details = document.querySelector('[id$="__details"].shopify-section, .shopify-section[id$="__details"]');
      // Not in the theme editor: there a section moved inside another reads as "Broken HTML" (Oct 13).
      if (this.details && !window.Shopify?.designMode) {
        this.detailsHome = document.createComment('details-home');
        this.details.before(this.detailsHome);
        this.placeDetails = () => {
          const copy = this.querySelector('.fdw-buy-copy');
          if (!this.mq.matches && copy) { copy.append(this.details); this.details.classList.add('fdw2-details-in-column'); }
          else { this.detailsHome.after(this.details); this.details.classList.remove('fdw2-details-in-column'); }
        };
        this.placeDetails();
        this.mq.addEventListener?.('change', this.placeDetails);
      }
      this.addEventListener('change', e => {
        if (e.target.name?.startsWith('tier-')) { this.tier = Number(e.target.value); this.render(); }
      });
      this.form.addEventListener('submit', e => { e.preventDefault(); this.submit(); });
      this.onKey = e => {
        if (!this.sheet) return;
        if (e.key === 'Escape') this.closeSheet(false);
        if (e.key === 'Tab') this.trapFocus(e);
      };
      document.addEventListener('keydown', this.onKey);
      this.render();
      // Once the page has loaded, fetch every pattern's first photo in the background so a swatch tap shows it instantly
      const warm = () => this.styles.forEach(st => { const src = st.images?.[0]?.src; if (src) Object.assign(new Image(), this.photoAttrs(src)); });
      const idle = () => (window.requestIdleCallback || (f => setTimeout(f, 300)))(warm);
      if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
    }

    // Time bar (Option A, Oct 12): which batch is open, and how far through its order window we are, comes from
    // the order cutoff dates, not from sales. The bar fills from presale open to the cutoff; after batch 1's cutoff,
    // batch 2 opens and the button and ships line switch to it. Nothing here claims a number of pairs sold.
    applyBatchTime() {
      const card = this.querySelector('[data-batches]');
      if (!card || card.dataset.mode !== 'live' || card.dataset.bar === 'sales') return;
      const endOf = d => d ? new Date(d + 'T23:59:59' + (d >= '2026-11-01' ? '-08:00' : '-07:00')) : null; // end of that day, Pacific
      const now = Date.now(), open = new Date(card.dataset.open || 0).getTime();
      const cut = [null, endOf(card.dataset.cut1), endOf(card.dataset.cut2)];
      const current = cut[1] && now > cut[1] ? 2 : 1;
      card.dataset.current = String(current);
      card.querySelectorAll('[data-step]').forEach(li => {
        const n = Number(li.dataset.step), pill = li.querySelector('.fdw2-tl-pill');
        li.classList.toggle('is-sold', n < current); li.classList.toggle('is-current', n === current); li.classList.toggle('is-next', n > current);
        if (pill) pill.textContent = n < current ? 'CLOSED' : n === current ? 'OPEN NOW' : 'UP NEXT';
        const dot = li.querySelector('.fdw2-tl-dot'); if (dot) dot.innerHTML = n < current ? '&#10003;' : String(n);
        const note = li.querySelector('.fdw2-tl-note');
        if (note) { note.hidden = n <= current; const prevCut = cut[n - 1]; if (prevCut) note.textContent = 'Opens after Batch ' + (n - 1) + ' orders close ' + prevCut.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' }) + '.'; }
        const bar = li.querySelector('[data-tl-bar]'), meta = li.querySelector('[data-tl-meta]');
        if (!bar || !meta) return;
        const end = cut[n], show = n === current;
        bar.hidden = meta.hidden = !show;
        if (!show) return;
        if (!end) { bar.hidden = true; meta.firstChild.textContent = 'Now reserving'; meta.lastChild.textContent = ''; return; }
        const start = n === 1 ? open : (cut[1]?.getTime() || open);
        const pct = Math.min(100, Math.max(4, Math.round((now - start) / (end - start) * 100)));
        bar.firstChild.style.width = pct + '%';
        bar.setAttribute('role', 'img'); bar.setAttribute('aria-label', 'Order window ' + pct + '% over');
        const days = Math.ceil((end - now) / 86400000);
        meta.firstChild.textContent = 'Orders close ' + end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });
        meta.lastChild.textContent = days <= 1 ? 'Last day' : days + ' days left';
      });
      // Ships line follows the open batch
      const ships = this.querySelector('.fdw2-ships'), date = card.dataset['ship' + current + 'Long'];
      if (ships && ships.dataset.shipsText && date) { const dot = ships.querySelector('i'); ships.textContent = ships.dataset.shipsText.replace('[ships]', date); if (dot) ships.prepend(dot); }
    }

    // Gallery photo sizes: phones take 1200 (smaller file), desktop up to 1600. The background warm-up uses the same set so it hits the cache.
    photoAttrs(src) {
      const at = w => src.replace(/width=1000/, 'width=' + w);
      return { src, srcset: src + ' 1000w, ' + at(1200) + ' 1200w, ' + at(1600) + ' 1600w', sizes: '(min-width: 750px) 57vw, 100vw' };
    }

    disconnectedCallback() { document.removeEventListener('keydown', this.onKey); }

    // Sneak peek signup: same Klaviyo list as the teaser page; the page confirms (no email is sent).
    setupSignup() {
      const box = this.querySelector('[data-signup]');
      const form = box?.querySelector('form');
      if (!form) return;
      form.addEventListener('submit', async e => {
        e.preventDefault();
        if (!form.reportValidity()) return;
        const status = form.querySelector('[role=status]'), button = form.querySelector('button'), input = form.querySelector('input');
        if (button.disabled) return;
        if (!/^[A-Za-z0-9]{6}$/.test(box.dataset.key) || !/^[A-Za-z0-9]{6}$/.test(box.dataset.list)) { status.textContent = 'Signup is not available yet. Please try again later.'; return; }
        button.disabled = true; status.textContent = 'Submitting…';
        try {
          const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(box.dataset.key), {
            method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2026-07-15' },
            body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: 'Winter Gloves Product Page', profile: { data: { type: 'profile', attributes: { email: input.value.trim(), subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } }, relationships: { list: { data: { type: 'list', id: box.dataset.list } } } } })
          });
          if (!res.ok) throw new Error('failed');
          const done = this.el('div', { class: 'fdw2-signup-done', role: 'status', tabindex: '-1' }, [
            this.el('span', { class: 'fdw2-signup-check', 'aria-hidden': 'true', text: '✓' }),
            this.el('strong', { text: box.dataset.successHeading || 'You’re on the list!' }),
            box.dataset.success ? this.el('p', { text: box.dataset.success }) : null
          ]);
          form.replaceWith(done); done.focus({ preventScroll: true });
        } catch (err) {
          status.textContent = 'We couldn’t complete your signup. Please try again.'; button.disabled = false;
        }
      });
    }

    // One entry per fit + pattern, built from the collection's products.
    groupProducts(products) {
      const fitOf = text => {
        const t = String(text || '').toLowerCase();
        if (/\b(women|womens|women's|woman|ladies)\b/.test(t)) return 'Women';
        if (/\b(men|mens|men's|man)\b/.test(t)) return 'Men';
        return '';
      };
      return products.flatMap(p => {
        const sizeIndex = (p.options || []).findIndex(n => /size/i.test(n));
        const fit = fitOf(p.title);
        if (!fit) return [];
        const name = p.title.split(/\s[-–]\s/).pop().trim();
        // Some products say XXL / XXXL, others 2XL / 3XL: same sizes, shown as 2XL / 3XL (Whitney: cleaner)
        const norm = v => ({ 'XXL': '2XL', 'XXXL': '3XL' })[String(v).toUpperCase()] || v;
        const variants = p.variants.map(v => ({ ...v, size: norm(sizeIndex >= 0 ? v.options[sizeIndex] : v.title) }));
        return [{ key: String(p.id), title: p.title, fit, cut: cutOf(p.type, p.title), name, image: p.image, swatch: p.swatch || null, photo: p.images?.[0]?.src || p.image, images: p.images, variants }];
      });
    }

    firstStyle(fit, cut) { return (this.styles.find(s => s.fit === fit && (!cut || s.cut === cut)) || this.styles.find(s => s.fit === fit))?.key || ''; }
    style(key) { return this.styles.find(s => s.key === key); }
    best(name, fit, cut) {
      const f = test => this.styles.find(test);
      // Always stays inside the fit: same pattern + style, same pattern, same style, then anything in that fit
      return f(s => s.name === name && s.fit === fit && s.cut === cut) || f(s => s.name === name && s.fit === fit)
        || f(s => s.fit === fit && s.cut === cut) || f(s => s.fit === fit);
    }
    priceOf(choice) { const s = this.style(choice.style); return (this.variant(choice) || s?.variants[0])?.price || 0; }
    sizesFor(fit) {
      const sizes = [...new Set(this.styles.filter(s => s.fit === fit).flatMap(s => s.variants.map(v => v.size)))];
      const rank = v => { const i = SIZE_ORDER.indexOf(String(v).toLowerCase()); return i < 0 ? 99 : i; };
      return sizes.sort((a, b) => rank(a) - rank(b));
    }
    variant(choice) { return this.style(choice.style)?.variants.find(v => v.size === choice.size) || null; }
    // In sneak peek nothing is for sale yet, so every size that exists shows as a normal choice.
    // Sold out pattern (Whitney, Oct 12): no size of it can be bought. Still tappable so the shopper sees the photos.
    styleOut(s) { return !!s && !this.prelaunch && !s.variants.some(v => v.available); }
    available(styleKey, size) { return !!this.style(styleKey)?.variants.some(v => v.size === size && (this.prelaunch || v.available)); }

    // Pair 2 follows the top until the customer swaps it.
    // Hoodie 2 and 3 follow the top until the shopper swaps them.
    syncPair2() {
      this.extras.forEach(x => { if (!x.custom) Object.assign(x, { fit: this.top.fit, cut: this.top.cut, size: this.top.size, style: this.top.style }); });
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
    }
    unitPrice() { return this.style(this.top.style)?.variants[0]?.price || 5999; }
    // 1 hoodie: its own price. 3 hoodies: the cheapest of the three is free.
    totals(n) {
      if (n !== 2) { const p = this.priceOf(this.top); return { full: p, paid: p, free: 0, freeIndex: -1 }; }
      const prices = [this.top, ...this.extras].map(c => this.priceOf(c));
      const min = Math.min(...prices), freeIndex = prices.lastIndexOf(min);
      const full = prices.reduce((a, b) => a + b, 0);
      return { full, paid: full - min, free: min, freeIndex, prices };
    }

    el(tag, attrs = {}, kids = []) {
      const e = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) {
        if (k === 'text') e.textContent = v;
        else if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
        else if (v !== false && v != null) e.setAttribute(k, v === true ? '' : v);
      }
      (Array.isArray(kids) ? kids : [kids]).forEach(c => c && e.append(c));
      return e;
    }
    img(src, cls, alt = '') { return src ? this.el('img', { src, alt, class: cls, loading: 'lazy', width: 240, height: 240 }) : this.el('span', { class: cls + ' fdw2-img-empty', 'aria-hidden': 'true' }); }

    // Same markup as Jarod's fd-winter-bundle choices, so fd-winter-lp.css styles them identically.
    buttons(legend, values, current, onPick, opts = {}) {
      const field = this.el('fieldset', { class: 'fdw-choice-field' });
      field.append(this.el('legend', {}, legend));
      const row = this.el('div', { class: opts.cls || 'fdw-option-buttons' });
      values.forEach(v => row.append(this.el('button', {
        type: 'button', class: 'fdw-option' + (v.out ? ' is-out' : ''), 'aria-pressed': String(v.value === current), disabled: v.disabled || false,
        'aria-label': (v.aria || v.label) + (v.out && !v.aria ? ', sold out' : ''), onclick: () => onPick(v.value)
      }, [v.image && v.out ? this.el('span', { class: 'fdw2-out-wrap' }, [v.image]) : v.image, this.el('span', { text: v.label })])));
      field.append(row);
      return field;
    }

    // Jarod's size guide <details> moves into the Size legend as a link.
    // Size guide as a pop-up from anywhere on the page: any link to #size-guide (e.g. the FAQ "Open the size guide")
    // borrows the same guide (tabs and all), shows it in a dialog, and puts it back on close.
    openSizeGuidePopup() {
      // Whitney's size guide (snippets/fd-size-guide) already opens as a drawer from its "Size Guide ?" trigger: use that
      const trigger = this.querySelector('[data-fdsg-open]');
      if (trigger) { trigger.click(); return true; }
      if (this.guide === undefined) { this.guide = this.querySelector('.fdw-size-guide'); this.guide?.remove(); }
      if (!this.guide) return false;
      const guide = this.guide, home = guide.parentNode, next = guide.nextSibling, wasOpen = guide.open, opener = document.activeElement;
      const close = this.el('button', { type: 'button', class: 'fdw2-sg-close', 'aria-label': 'Close size guide' });
      close.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M4 4l16 16M20 4 4 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      const card = this.el('div', { class: 'fdw2-sg-card' }, [close, guide]);
      const box = this.el('div', { class: 'fdw2-sg', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Size guide' }, card);
      guide.open = true;
      const done = () => { box.remove(); document.removeEventListener('keydown', onKey); document.documentElement.style.overflow = ''; guide.open = wasOpen; if (home) home.insertBefore(guide, next); opener?.focus?.(); };
      const onKey = e => { if (e.key === 'Escape') done(); };
      close.onclick = done; box.addEventListener('click', e => { if (e.target === box) done(); });
      document.body.append(box); document.documentElement.style.overflow = 'hidden'; document.addEventListener('keydown', onKey); close.focus();
      return true;
    }

    sizeGuideLink(field) {
      if (this.guide === undefined) { this.guide = this.querySelector('.fdw-size-guide'); this.guide?.remove(); }
      if (!this.guide) return;
      const legend = field.querySelector('legend');
      const link = this.el('button', { type: 'button', class: 'fdw-size-guide-link', 'aria-expanded': String(this.guide.open), text: this.guide.querySelector('summary')?.textContent || 'Size guide',
        onclick: () => { this.guide.open = !this.guide.open; link.setAttribute('aria-expanded', String(this.guide.open)); } });
      legend.append(link);
      field.insertBefore(this.guide, field.querySelector('.fdw-option-buttons'));
    }

    renderTop() {
      const top = this.querySelector('[data-global-options]');
      top.replaceChildren();
      const current = this.style(this.top.style);
      const set = (s, keepSize) => {
        if (!s) return;
        const fitChanged = s.fit !== this.top.fit;
        this.top = { fit: s.fit, cut: s.cut, size: keepSize && !fitChanged ? this.top.size : '', style: s.key };
        if (this.top.size && !this.available(s.key, this.top.size)) this.top.size = '';
        this.render();
        if (fitChanged) this.dispatchEvent(new CustomEvent('fd:fit-change', { bubbles: true, detail: { fit: s.fit } }));
      };
      const inFit = this.styles.filter(s => s.fit === this.top.fit);
      // Only the patterns sold in the chosen Style (Whitney, Oct 12): Crew shows crew patterns, Zip shows zip patterns.
      const inCut = inFit.filter(s => s.cut === this.top.cut);
      const names = [...new Set((inCut.length ? inCut : inFit).map(s => s.name))];
      // Sold-out colors go last (Whitney, Oct 12); the rest keep their order
      names.sort((x, y) => this.styleOut(this.best(x, this.top.fit, this.top.cut)) - this.styleOut(this.best(y, this.top.fit, this.top.cut)));
      top.append(this.buttons(this.el('span', {}, ['Color', current ? ': ' : '', current ? this.el('span', { class: 'fdw2-pattern-name', text: current.name }) : null]),
        names.map(name => { const s = this.best(name, this.top.fit, this.top.cut); return { value: name, label: name, out: this.styleOut(s), image: this.fabric ? this.fabricImg(s) : this.img(s.image, 'fdw2-swatch-img') }; }),
        current?.name, name => set(this.best(name, this.top.fit, this.top.cut), true),
        { cls: 'fdw-swatches' + (this.fabric ? ' fdw2-fabric fdw2-' + this.swatchStyle : '') }));
      // Style: keeps the pattern when it comes in that style, otherwise the first pattern in that style (same fit)
      top.append(this.buttons('Style', CUTS.filter(c => inFit.some(s => s.cut === c)).map(c => ({ value: c, label: c })), this.top.cut,
        cut => { if (cut !== this.top.cut) set(inFit.find(s => s.name === current?.name && s.cut === cut) || inFit.find(s => s.cut === cut), true); },
        { cls: 'fdw-option-buttons fdw2-fit fdw2-cut' }));
      if (this.top.fit) {
        const sizeField = this.buttons('Size', this.sizesFor(this.top.fit).map(s => ({ value: s, label: s, disabled: !this.available(this.top.style, s), out: !this.available(this.top.style, s), aria: s + (this.available(this.top.style, s) ? '' : ', sold out') })), this.top.size, size => { this.top.size = size; this.render(); }, { cls: 'fdw-option-buttons fdw2-sizes' });
        this.sizeGuideLink(sizeField);
        top.append(sizeField);
      }
      top.append(this.buttons('Fit', FITS.filter(([f]) => this.styles.some(s => s.fit === f)).map(([value, label]) => ({ value, label })), this.top.fit,
        fit => { if (fit !== this.top.fit) set(this.best(current?.name, fit, this.top.cut), false); }, { cls: 'fdw-option-buttons fdw2-fit' }));
    }
    // Fabric swatch: theme file fd-swatch-<pattern>.jpg (e.g. fd-swatch-topo-geo.jpg); falls back to the glove photo if missing
    get fabric() { return this.swatchStyle === 'round' || this.swatchStyle === 'square'; }
    fabricImg(s) {
      const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const src = s.swatch || (this.dataset.swatchBase || '').replace('PATTERN', slug);
      return this.el('img', { src: src || s.image, alt: '', class: 'fdw2-swatch-img', loading: 'lazy', width: 160, height: 160, onerror: e => { if (s.image && e.target.src !== s.image) e.target.src = s.image; } });
    }

    pairRow(n, choice, swap, price, isFree) {
      const s = this.style(choice.style);
      const meta = fitLabel(choice.fit) + ' · ' + (s?.cut || choice.cut) + ' · ' + (choice.size ? 'Size ' + choice.size : 'Size?');
      return this.el('div', { class: 'fdw2-pair' + (isFree ? ' is-free' : '') }, [
        this.img(s?.image, 'fdw2-pair-img', s?.name || ''),
        this.el('div', { class: 'fdw2-pair-body' }, [
          this.el('strong', {}, ['Hoodie ' + n + ': ', this.el('span', { class: 'fdw2-pattern-name', text: s?.name || '' })]),
          this.el('small', { class: choice.size ? '' : 'fdw2-needs', text: meta }),
          this.el('span', { class: 'fdw2-pair-price' }, isFree ? [this.el('s', { text: this.money(price) }), this.el('b', { class: 'fdw2-free-tag', text: 'FREE' })] : [this.el('b', { text: this.money(price) })])
        ]),
        swap
      ]);
    }

    renderPairs() {
      const wrap = this.querySelector('[data-pairs]');
      wrap.replaceChildren();
      wrap.hidden = this.tier !== 2;
      if (this.tier !== 2) return;
      const t = this.totals(2);
      wrap.append(this.pairRow(1, this.top, null, t.prices[0], t.freeIndex === 0));
      this.swapButtons = this.extras.map((x, k) => this.el('button', {
        type: 'button', class: 'fdw2-swap', 'aria-haspopup': 'dialog', 'aria-label': 'Swap hoodie ' + (k + 2) + ' for a different pattern, style or size',
        disabled: !this.top.fit, onclick: () => this.openSheet(k)
      }, [this.el('span', { 'aria-hidden': 'true', class: 'fdw2-swap-icon', text: '⇄' }), this.el('span', { text: 'Swap' })]));
      this.extras.forEach((x, k) => wrap.append(this.pairRow(k + 2, x, this.swapButtons[k], t.prices[k + 1], t.freeIndex === k + 1)));
      wrap.append(this.el('p', { class: 'fdw2-free-note', text: 'The lowest priced hoodie of the three is free.' }));
    }

    // Picker sheet: opens filtered to Pair 2's fit and size. Only in-stock patterns show.
    openSheet(k = 0) {
      const x = this.extras[k];
      this.sheet = { k, fit: x.fit, cut: x.cut, size: x.size, style: x.style, show: '' };
      document.documentElement.classList.add('fdw2-locked');
      this.renderSheet();
      this.querySelector('.fdw2-sheet h3')?.focus();
    }
    closeSheet(apply) {
      const k = this.sheet?.k || 0;
      if (apply && this.sheet.size && this.available(this.sheet.style, this.sheet.size)) {
        const { fit, cut, size, style } = this.sheet;
        this.extras[k] = { fit, cut, size, style, custom: fit !== this.top.fit || cut !== this.top.cut || size !== this.top.size || style !== this.top.style };
      }
      this.sheet = null;
      document.documentElement.classList.remove('fdw2-locked');
      this.querySelector('[data-sheet]')?.remove();
      this.render();
      this.swapButtons?.[k]?.focus();
    }
    trapFocus(e) {
      const nodes = [...this.querySelectorAll('.fdw2-sheet button:not([disabled]), .fdw2-sheet h3')];
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    renderSheet() {
      // Keep keyboard focus on the same control after redrawing.
      const was = document.activeElement?.closest?.('.fdw2-sheet') ? document.activeElement : null;
      const refocus = was ? { cls: was.className, text: was.textContent } : null;
      this.querySelector('[data-sheet]')?.remove();
      if (!this.sheet) return;
      const d = this.sheet, label = 'Hoodie ' + (d.k + 2);
      const sheetId = 'fdw2-sheet-' + (this.closest('section')?.id || 'x');
      const chip = (text, key, name) => this.el('button', {
        type: 'button', class: 'fdw2-filter', 'aria-expanded': String(d.show === key), 'aria-label': name ? name + ': ' + text + ', change' : null,
        onclick: () => { d.show = d.show === key ? '' : key; this.renderSheet(); }
      }, [this.el('span', { text }), this.el('i', { 'aria-hidden': 'true', text: '▾' })]);
      const body = this.el('div', { class: 'fdw2-sheet', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': sheetId + '-title' });
      body.append(this.el('div', { class: 'fdw2-sheet-head' }, [
        this.el('div', {}, [this.el('small', { text: label.toUpperCase() }), this.el('h3', { id: sheetId + '-title', tabindex: '-1', text: 'Choose ' + label.toLowerCase() })]),
        this.el('button', { type: 'button', class: 'fdw2-sheet-close', 'aria-label': 'Close', text: '×', onclick: () => this.closeSheet(false) })
      ]));
      // Fit / Style / Size as labeled dropdown boxes, plus a line saying they only change this hoodie (Whitney, Oct 12: option D)
      const pick = (name, text, key) => this.el('div', { class: 'fdw2-sel' }, [this.el('span', { class: 'fdw2-sel-label', 'aria-hidden': 'true', text: name }), chip(text, key, name)]);
      body.append(this.el('div', { class: 'fdw2-filters fdw2-selects' }, [pick('Fit', fitLabel(d.fit), 'fit'), pick('Style', d.cut, 'cut'), pick('Size', d.size || 'Choose', 'size')]));
      body.append(this.el('p', { class: 'fdw2-sel-hint', text: 'Your choices here are for ' + label.toLowerCase() + '.' }));
      if (d.show === 'fit') body.append(this.buttons('Fit for ' + label, FITS.filter(([f]) => this.styles.some(s => s.fit === f)).map(([value, l]) => ({ value, label: l })), d.fit, fit => {
        if (fit !== d.fit) { d.fit = fit; d.size = ''; d.style = ''; if (!this.styles.some(s => s.fit === fit && s.cut === d.cut)) d.cut = this.styles.find(s => s.fit === fit)?.cut || d.cut; d.show = 'size'; } else d.show = '';
        this.renderSheet();
      }));
      if (d.show === 'cut') body.append(this.buttons('Style for ' + label, CUTS.filter(c => this.styles.some(s => s.fit === d.fit && s.cut === c)).map(c => ({ value: c, label: c })), d.cut, cut => {
        if (cut !== d.cut) { d.cut = cut; d.style = ''; } d.show = ''; this.renderSheet();
      }));
      if (d.show === 'size') body.append(this.buttons('Size for ' + label, this.sizesFor(d.fit).map(s => ({ value: s, label: s })), d.size, size => {
        d.size = size; d.show = ''; if (!this.available(d.style, size)) d.style = ''; this.renderSheet();
      }));
      const grid = this.el('div', { class: 'fdw2-sheet-grid', role: 'radiogroup', 'aria-label': 'Patterns in stock' });
      if (!d.size) grid.append(this.el('p', { class: 'fdw2-hint', text: 'Choose a size to see what is in stock.' }));
      else {
        // Every pattern in this fit and style shows; ones sold out in the chosen size are gray with a line and can't be picked
        const all = this.styles.filter(s => s.fit === d.fit && s.cut === d.cut)
          .sort((x, y) => this.available(y.key, d.size) - this.available(x.key, d.size));   // sold out in this size goes last
        const inStock = all.filter(s => this.available(s.key, d.size));
        if (!inStock.some(s => s.key === d.style)) d.style = inStock[0]?.key || '';
        if (!inStock.length) grid.append(this.el('p', { class: 'fdw2-hint', text: 'Nothing in stock in this size and style right now.' }));
        all.forEach(s => { const ok = this.available(s.key, d.size); grid.append(this.el('button', {
          type: 'button', role: 'radio', class: 'fdw2-tile' + (ok ? '' : ' is-out'), 'aria-checked': String(s.key === d.style), disabled: !ok,
          'aria-label': s.name + (ok ? '' : ', sold out in size ' + d.size),
          onclick: () => { d.style = s.key; this.renderSheet(); }
        }, [this.el('span', { class: 'fdw2-tile-imgwrap' }, [this.img(s.photo, 'fdw2-tile-img', '')]), this.el('span', { class: 'fdw2-pattern-name', text: s.name }), this.el('small', { class: 'fdw2-tile-price', text: ok ? this.money(s.variants[0]?.price || 0) : 'Sold out' })])); });
      }
      body.append(grid);
      const ready = d.size && this.available(d.style, d.size);
      body.append(this.el('button', { type: 'button', class: 'fdw-button fdw2-sheet-use', disabled: !ready, text: 'USE THIS HOODIE', onclick: () => this.closeSheet(true) }));
      const layer = this.el('div', { class: 'fdw2-sheet-layer', 'data-sheet': true }, [this.el('div', { class: 'fdw2-sheet-backdrop', onclick: () => this.closeSheet(false) }), body]);
      this.append(layer);
      const same = refocus && [...body.querySelectorAll('button')].find(b => b.className === refocus.cls && b.textContent === refocus.text);
      (same || body.querySelector('h3'))?.focus();
    }

    renderTiers() {
      this.querySelectorAll('[data-tier]').forEach(t => {
        const n = Number(t.dataset.tier), active = n === this.tier, money = this.totals(n);
        t.classList.toggle('is-selected', active);
        t.querySelector('input').checked = active;
        t.querySelector('[data-price]').textContent = this.money(money.paid);
        t.querySelector('[data-compare]').textContent = money.full > money.paid ? this.money(money.full) : '';
        t.querySelector('[data-badge]').textContent = '';
      });
    }

    renderGallery() {
      const s = this.style(this.top.style);
      if (this.galleryKey === s?.key) return;
      this.galleryKey = s?.key;
      // Two-line title: pattern name over the product line (from "Insulated Winter Gloves - Women's - Dark Rose")
      const pattern = this.querySelector('[data-title-pattern]'), line = this.querySelector('[data-title-line]');
      if (pattern && s) pattern.textContent = s.name;
      if (line && s) line.textContent = s.title.split(/\s[-–]\s/)[0];
      const main = this.querySelector('[data-gallery-main]'), thumbs = this.querySelector('[data-gallery-thumbs]');
      main.replaceChildren(); thumbs.replaceChildren();
      let dots = this.querySelector('[data-gallery-dots]');
      if (!dots) { dots = this.el('div', { class: 'fdw2-dots', 'data-gallery-dots': true, 'aria-hidden': 'true' }); thumbs.before(dots); }
      dots.replaceChildren();
      const images = s?.images || [];
      let current = 0;
      // All photos sit side by side on one strip; on phones the strip follows the finger so the next photo slides in (Poncho style)
      // First two photos load right away; the rest load one step ahead of the shopper (see show)
      const slides = images.map((image, i) => this.el('div', { class: 'fdw2-slide' }, this.el('img', { ...this.photoAttrs(image.src), alt: image.alt || s.name, width: 1000, height: 1000, loading: i < 2 ? 'eager' : 'lazy', draggable: 'false' })));
      // The studio shot is the same render for every pattern; a small fixed nudge per pattern makes a swatch tap
      // feel like a different photo instead of a recolor (Whitney, Oct 12). Main photo only; hotspots keep exact positions.
      if (this.dataset.nudge !== 'false' && slides[0]) {
        const NUDGE = [[0, 0, 0], [-2.5, 1.5, -1.6], [2.2, -1.2, 1.3], [-1.6, 2.2, -0.9], [1.4, 1.8, 1.8]];
        const names = [...new Set(this.styles.map(st => st.name))];
        const [x, y, r] = NUDGE[Math.max(0, names.indexOf(s.name)) % NUDGE.length];
        slides[0].firstChild.style.transform = `translate(${x}%, ${y}%) rotate(${r}deg)`;
      }
      const track = this.el('div', { class: 'fdw2-track' }, slides);
      const zoomBtn = this.el('button', { type: 'button', class: 'fdw2-zoom-hint', 'aria-label': 'Zoom in on this photo', onclick: () => this.openZoom(images, current, s.name) });
      zoomBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M15.5 15.5L21 21M10.5 7.5v6M7.5 10.5h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      main.replaceChildren(track, zoomBtn);
      const place = (dx = 0, animate = true) => { track.classList.toggle('is-moving', animate); track.style.transform = `translateX(calc(${-current * 100}% + ${dx}px))`; };
      const show = (i, animate = true) => {
        current = i;
        [i - 1, i + 1, i + 2].forEach(k => slides[k]?.firstChild.setAttribute('loading', 'eager')); // next photos ready before the swipe
        place(0, animate);
        thumbs.querySelectorAll('button').forEach((b, j) => b.setAttribute('aria-pressed', String(j === i)));
        dots.querySelectorAll('span').forEach((d, j) => d.classList.toggle('is-on', j === i));
      };
      let dragged = false;
      track.addEventListener('click', () => { if (!dragged) this.openZoom(images, current, s.name); });
      images.forEach((image, i) => thumbs.append(this.el('button', { type: 'button', 'aria-label': 'View product image ' + (i + 1), onclick: () => show(i) }, this.el('img', { src: image.src, alt: '', loading: 'lazy', width: 64, height: 64 }))));
      if (images.length > 1) images.forEach(() => dots.append(this.el('span')));
      // Desktop (Poncho style): every photo after the first sits in a 2-column grid under the big one. CSS shows it at 750px and up.
      let grid = this.querySelector('[data-gallery-grid]');
      if (!grid) { grid = this.el('div', { class: 'fdw2-grid', 'data-gallery-grid': true }); this.querySelector('.fdw-gallery-stage').after(grid); }
      grid.replaceChildren(...images.slice(1).map((image, k) => this.el('button', { type: 'button', class: 'fdw2-grid-item', 'aria-label': 'Zoom in on product image ' + (k + 2), onclick: () => this.openZoom(images, k + 1, s.name) }, this.el('img', { src: image.src, alt: image.alt || s.name, loading: 'lazy', width: 1000, height: 1000 }))));
      // The feature hotspot section swaps its photo to the chosen pattern
      if (s) document.dispatchEvent(new CustomEvent('fd:pattern-change', { detail: { image: s.photo, name: s.name, fit: s.fit, cut: s.cut } }));
      const prev = this.querySelector('[data-gallery-prev]'), next = this.querySelector('[data-gallery-next]');
      prev.hidden = next.hidden = images.length < 2;
      prev.onclick = () => show((current - 1 + images.length) % images.length);
      next.onclick = () => show((current + 1) % images.length);
      // Swipe on the photo (phones): the strip follows the finger, then settles on the nearest photo
      let t0 = null;
      main.ontouchstart = e => { if (images.length < 2) return; t0 = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: performance.now(), dir: null }; dragged = false; place(0, false); };
      const onMove = e => {
        if (!t0) return; let dx = e.touches[0].clientX - t0.x; const dy = e.touches[0].clientY - t0.y;
        if (!t0.dir && Math.abs(dx) + Math.abs(dy) > 8) t0.dir = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (t0.dir !== 'x') return; // vertical: let the page scroll
        e.preventDefault(); dragged = true;
        if ((current === 0 && dx > 0) || (current === images.length - 1 && dx < 0)) dx *= 0.35;
        place(dx, false);
      };
      main.ontouchend = e => {
        if (!t0) return; const d = t0; t0 = null;
        if (d.dir !== 'x') { place(0); return; }
        const dx = e.changedTouches[0].clientX - d.x, fast = Math.abs(dx) / (performance.now() - d.t) > 0.5, far = Math.abs(dx) > main.offsetWidth * 0.2;
        show(Math.max(0, Math.min(images.length - 1, current + ((fast || far) ? (dx < 0 ? 1 : -1) : 0))));
        setTimeout(() => { dragged = false; }, 50);
      };
      // passive: false so a sideways swipe doesn't also scroll the page; swap the old listener when the pattern changes
      if (this.galleryMove) main.removeEventListener('touchmove', this.galleryMove);
      this.galleryMove = onMove; main.addEventListener('touchmove', onMove, { passive: false });
      if (images.length) show(0, false);
      // On desktop the big photo is always the first one (the rest are in the grid)
      this.desktopMq ??= window.matchMedia('(min-width: 750px)');
      this.desktopMq.onchange = e => { if (e.matches && images.length) show(0, false); };
    }

    // Full-screen photo viewer: tap the photo to zoom in at that spot, move to look around, tap again to zoom out.
    openZoom(images, start, name) {
      if (!images.length) return;
      const big = src => src + (src.includes('?') ? '&' : '?') + 'width=2048';
      const ARROW_L = '<svg viewBox="0 0 44 24" width="44" height="24" aria-hidden="true"><path d="M43 12H2M12 2 2 12l10 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      const ARROW_R = '<svg viewBox="0 0 44 24" width="44" height="24" aria-hidden="true"><path d="M1 12h41M32 2l10 10-10 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      const CLOSE = '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M3 3l18 18M21 3 3 21" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      const SCALE = 2.5, n = images.length;
      let i = start, zoomed = false, tx = 0, ty = 0;
      const opener = document.activeElement;
      const box = this.el('div', { class: 'fdw2-zoom', role: 'dialog', 'aria-modal': 'true', 'aria-label': name + ' photos' });
      const close = this.el('button', { type: 'button', class: 'fdw2-zoom-close', 'aria-label': 'Close' }); close.innerHTML = CLOSE;
      const prev = this.el('button', { type: 'button', class: 'fdw2-zoom-arrow fdw2-zoom-prev', 'aria-label': 'Previous photo' }); prev.innerHTML = ARROW_L;
      const next = this.el('button', { type: 'button', class: 'fdw2-zoom-arrow fdw2-zoom-next', 'aria-label': 'Next photo' }); next.innerHTML = ARROW_R;
      // Every photo sits side by side on one strip that follows the hand, so the next photo slides in while you drag (Poncho style)
      const slides = images.map(im => this.el('div', { class: 'fdw2-zoom-slide' }, this.el('img', { alt: im.alt || name, draggable: 'false' })));
      const track = this.el('div', { class: 'fdw2-zoom-track' }, slides);
      const frame = this.el('div', { class: 'fdw2-zoom-frame' }, track);
      const stage = this.el('div', { class: 'fdw2-zoom-stage' }, frame);
      const imgAt = k => slides[k].firstChild;
      const load = k => { const im = imgAt((k + n) % n); if (!im.src) im.src = big(images[(k + n) % n].src); };
      const place = (dx = 0, animate = true) => { track.classList.toggle('is-moving', animate); track.style.transform = `translateX(calc(${-i * 100}% + ${dx}px))`; };
      const clamp = () => { const im = imgAt(i), mx = im.offsetWidth * (SCALE - 1) / 2, my = im.offsetHeight * (SCALE - 1) / 2; tx = Math.max(-mx, Math.min(mx, tx)); ty = Math.max(-my, Math.min(my, ty)); };
      const paint = () => { const im = imgAt(i); im.style.transform = zoomed ? `translate(${tx}px, ${ty}px) scale(${SCALE})` : ''; im.classList.toggle('is-zoomed', zoomed); frame.classList.toggle('is-zoomed', zoomed); };
      const zoomAt = (cx, cy) => { const r = imgAt(i).getBoundingClientRect(); zoomed = true; tx = -(cx - (r.left + r.width / 2)) * (SCALE - 1); ty = -(cy - (r.top + r.height / 2)) * (SCALE - 1); clamp(); paint(); };
      const unzoom = () => { zoomed = false; tx = ty = 0; paint(); };
      const go = (k, animate = true) => { if (zoomed) unzoom(); i = Math.max(0, Math.min(n - 1, k)); [i - 1, i, i + 1].forEach(load); place(0, animate); prev.disabled = i === 0; next.disabled = i === n - 1; };
      // One pointer handler for mouse and touch: tap zooms in/out, drag pans when zoomed, otherwise drags the strip
      let down = null;
      frame.addEventListener('pointerdown', e => { e.preventDefault(); down = { x: e.clientX, y: e.clientY, t: performance.now(), tx, ty, moved: false }; frame.setPointerCapture(e.pointerId); frame.classList.add('is-dragging'); if (!zoomed) place(0, false); });
      frame.addEventListener('pointermove', e => {
        if (!down) return; let dx = e.clientX - down.x; const dy = e.clientY - down.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) down.moved = true;
        if (zoomed) { tx = down.tx + dx; ty = down.ty + dy; clamp(); paint(); return; }
        if ((i === 0 && dx > 0) || (i === n - 1 && dx < 0)) dx *= 0.35; // soft stop at the first and last photo
        place(dx, false);
      });
      const up = e => {
        if (!down) return; const d = down, dx = e.clientX - d.x; down = null; frame.classList.remove('is-dragging');
        if (!d.moved) { if (zoomed) unzoom(); else zoomAt(e.clientX, e.clientY); place(0); return; }
        if (zoomed) return;
        const fast = Math.abs(dx) / (performance.now() - d.t) > 0.5; // a quick flick counts even if short
        const far = Math.abs(dx) > frame.offsetWidth * 0.2;
        go(i + ((fast || far) ? (dx < 0 ? 1 : -1) : 0));
      };
      frame.addEventListener('pointerup', up);
      frame.addEventListener('pointercancel', () => { down = null; frame.classList.remove('is-dragging'); if (!zoomed) place(0); });
      stage.addEventListener('click', e => { if (e.target === stage) done(); }); // click on the background closes
      const done = () => { box.remove(); document.removeEventListener('keydown', onKey); document.documentElement.style.overflow = ''; opener?.focus?.(); };
      const onKey = e => { if (e.key === 'Escape') done(); if (e.key === 'ArrowLeft') go(i - 1); if (e.key === 'ArrowRight') go(i + 1); };
      close.onclick = done; prev.onclick = () => go(i - 1); next.onclick = () => go(i + 1);
      prev.hidden = next.hidden = n < 2;
      box.append(stage, close, prev, next);
      document.body.append(box);
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      go(start, false); close.focus();
    }

    missing() {
      if (!this.top.fit) return 'Choose your fit';
      if (!this.top.size) return 'Choose your size';
      if (!this.variant(this.top)?.available) return 'That size is sold out in this pattern';
      if (this.tier === 2) for (const [k, x] of this.extras.entries()) {
        if (!x.size) return 'Choose a size for hoodie ' + (k + 2);
        if (!this.variant(x)?.available) return 'Hoodie ' + (k + 2) + ' is sold out in that size. Tap Swap to pick another.';
      }
      return '';
    }

    render() {
      if (!this.styles.length) {
        if (this.status) this.status.textContent = window.Shopify?.designMode ? 'Choose a collection with Men’s or Women’s hoodie products.' : '';
        if (this.button) this.button.disabled = true;
        return;
      }
      this.syncPair2();
      this.renderTop();
      this.renderGallery();
      // Regular price under the title (sneak peek shows this price only, no offer)
      const unit = this.variant(this.top)?.price || this.unitPrice();
      const priceLine = this.querySelector('[data-price-line]'); if (priceLine) priceLine.textContent = this.money(unit);
      if (this.prelaunch) {
        const pp = this.querySelector('[data-preorder-price]'), po = this.querySelector('[data-preorder-original]');
        if (pp) pp.textContent = this.money(unit);
        if (po) po.textContent = '';
        return;
      }
      this.renderTiers();
      this.renderPairs();
      const need = this.missing();
      const enabled = this.dataset.enabled === 'true';
      this.button.disabled = this.busy || !!need || !enabled;
      const money = this.totals(this.tier);
      // "Pre-Order Batch 1 · $107.98": the batch number comes from the batch card, so it reads Batch 2 once batch 1 sells out
      const batchNo = this.querySelector('[data-batches]')?.dataset.current;
      this.label.textContent = this.busy ? 'ADDING...' : this.dataset.cta + ' · ' + this.money(money.paid);
      const preorderPrice = this.querySelector('[data-preorder-price]'), preorderOriginal = this.querySelector('[data-preorder-original]');
      if (preorderPrice) preorderPrice.textContent = this.money(money.paid);
      if (preorderOriginal) preorderOriginal.textContent = this.discount ? this.money(money.full) : '';
      if (!this.busy) this.status.textContent = need;
    }

    async submit() {
      if (this.busy || !this.button || this.button.disabled) return;
      const choices = this.tier === 2 ? [this.top, ...this.extras] : [this.top];
      // Same hoodie (style, color, size) more than once = one cart line with that quantity (Whitney, Oct 14)
      const byId = new Map();
      choices.forEach(c => { const id = this.variant(c).id; byId.set(id, (byId.get(id) || 0) + 1); });
      const items = [...byId].map(([id, quantity]) => ({ id, quantity, properties: this.tier === 2 ? { '_FD Bundle': 'Buy 2 Get 1' } : {} }));
      this.busy = true; this.render();
      try {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Unable to add these hoodies. Please try again.');
        this.busy = false; this.render();
        // Open the slider cart; the theme listens for ajaxProduct:added and rebuilds + opens the drawer.
        if (document.getElementById('CartDrawer')) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
        } else {
          window.location.assign(root + 'cart');
        }
      } catch (err) {
        this.busy = false; this.render(); this.status.textContent = err.message;
      }
    }
  }
  customElements.define('fd-uvh-box', UvhBox);

  // Batch card countdown: days until the presale opens (sneak peek) or until the current batch ships.
  const batchCountdown = () => document.querySelectorAll('[data-batches]').forEach(card => {
    const out = card.querySelector('[data-batches-count]'); if (!out) return;
    const raw = card.dataset.mode === 'pre' ? card.dataset.open : card.dataset['ship' + card.dataset.current];
    if (!raw) { out.textContent = ''; return; }
    const when = new Date(raw.length === 10 ? raw + 'T12:00:00-08:00' : raw);
    if (isNaN(when)) { out.textContent = ''; return; }
    const days = Math.ceil((when - Date.now()) / 86400000);
    out.textContent = days > 1 ? ' IN ' + days + ' DAYS' : days === 1 ? ' IN 1 DAY' : days === 0 ? ' TODAY' : '';
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', batchCountdown); else batchCountdown();
  document.addEventListener('shopify:section:load', batchCountdown);

  // Hero rotating phrase on this page: reserve the longest phrase's width so the box never jumps or wraps.
  const sizePhrases = () => document.querySelectorAll('fd-rotating-phrase').forEach(el => {
    try {
      const phrases = JSON.parse(el.querySelector('[data-phrases]')?.textContent || '[]').filter(Boolean);
      const longest = Math.max(0, ...phrases.map(p => String(p).length));
      if (longest) el.style.setProperty('--phrase-ch', longest + 'ch');
      // Keep prefix + phrase on one line: shrink the text a little if it doesn't fit the screen
      el.style.fontSize = '';
      const room = el.parentElement ? el.parentElement.clientWidth : 0;
      let size = parseFloat(getComputedStyle(el).fontSize);
      for (let i = 0; room && el.scrollWidth > room && size > 11 && i < 12; i++) { size -= 1; el.style.fontSize = size + 'px'; }
    } catch (e) { /* leave the default width */ }
  });
  const run = () => (document.fonts?.ready || Promise.resolve()).then(sizePhrases);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  window.addEventListener('resize', sizePhrases);
})();
